import { useCanvasKeyboardShortcuts } from '../use-canvas-keyboard-shortcuts'
import { useZMarqueeKeyRelease } from '../use-z-marquee-key-release'
import type {
  CanvasEditModes,
  CanvasFoundation,
  CanvasPointerGestures,
  CanvasSelectionCommands,
  CanvasTextLinking,
  CanvasToolModes,
} from '../../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../../types/resolved-center-canvas-config'

/**
 * Canvas keyboard shortcuts, and ending Z-marquee zoom when the Z key is released.
 */
export function useCanvasKeyboard(
  {
    bleedWidth,
    bleedHeight,
    onNodeEditModeChange,
    onLayerReorder,
    onUnifiedLayerArrange,
    activeItem,
    onBackgroundClick,
    onActivateSelectTool,
    onSelectAllApplied,
    onSelectNoneApplied,
    onFreezeSelected,
    onDeleteShapesHandled,
    nudgeDistancePt,
    onShapesNudge,
    isPanModeActive,
    onDeactivatePanMode,
    isTextToolActive,
    onActivateTextTool,
  }: ResolvedCenterCanvasConfig,
  {
    internalShapeManagerRef,
    canUnifiedBringForward,
    canUnifiedSendBackward,
    scrollRef,
    isPanInteractionActive,
    updateGuides,
    setSelectedGuideIds,
    selectedGuideIdsRef,
  }: Pick<
    CanvasFoundation,
    | 'internalShapeManagerRef'
    | 'canUnifiedBringForward'
    | 'canUnifiedSendBackward'
    | 'scrollRef'
    | 'isPanInteractionActive'
    | 'updateGuides'
    | 'setSelectedGuideIds'
    | 'selectedGuideIdsRef'
  >,
  {
    isTextLinkingActive,
    cancelTextLinking,
  }: Pick<CanvasTextLinking, 'isTextLinkingActive' | 'cancelTextLinking'>,
  {
    isInShapeCreationMode,
    isInPenToolMode,
    isInTextCreationMode,
  }: Pick<CanvasToolModes, 'isInShapeCreationMode' | 'isInPenToolMode' | 'isInTextCreationMode'>,
  {
    nodeEditShapeId,
    setNodeEditShapeId,
    setNodeEditShapeData,
    isNodeEditMode,
    exitNodeEditMode,
    setGroupIsolationPath,
    clearShapeSelectionWithHistory,
    clearUnifiedSelectionWithHistory,
    exitGroupIsolationMode,
    activeIsolationGroupId,
    lockSelectedObjects,
    unlockSelectedObjects,
    toggleFreezeSelectedObjects,
    canSelectShapes,
  }: Pick<
    CanvasEditModes,
    | 'nodeEditShapeId'
    | 'setNodeEditShapeId'
    | 'setNodeEditShapeData'
    | 'isNodeEditMode'
    | 'exitNodeEditMode'
    | 'setGroupIsolationPath'
    | 'clearShapeSelectionWithHistory'
    | 'clearUnifiedSelectionWithHistory'
    | 'exitGroupIsolationMode'
    | 'activeIsolationGroupId'
    | 'lockSelectedObjects'
    | 'unlockSelectedObjects'
    | 'toggleFreezeSelectedObjects'
    | 'canSelectShapes'
  >,
  {
    copySelectionToClipboard,
    cutSelectionToClipboard,
    pasteFromClipboard,
    cutSelectionForActiveMode,
    isTextEditSessionActive,
    refocusActiveTextEditor,
    selectAllForActiveTextEdit,
    handleDeleteKeyWhileTextEditing,
    deleteSelectedObjects,
    isEditableTarget,
  }: Pick<
    CanvasSelectionCommands,
    | 'copySelectionToClipboard'
    | 'cutSelectionToClipboard'
    | 'pasteFromClipboard'
    | 'cutSelectionForActiveMode'
    | 'isTextEditSessionActive'
    | 'refocusActiveTextEditor'
    | 'selectAllForActiveTextEdit'
    | 'handleDeleteKeyWhileTextEditing'
    | 'deleteSelectedObjects'
    | 'isEditableTarget'
  >,
  {
    setIsMarqueeZoomMode,
    zMarqueeKeyHeldRef,
    setZMarqueeKeyHeld,
    setMarqueeZoom,
    applyPanScroll,
  }: Pick<
    CanvasPointerGestures,
    | 'setIsMarqueeZoomMode'
    | 'zMarqueeKeyHeldRef'
    | 'setZMarqueeKeyHeld'
    | 'setMarqueeZoom'
    | 'applyPanScroll'
  >
) {
  useZMarqueeKeyRelease({ isEditableTarget, zMarqueeKeyHeldRef, setZMarqueeKeyHeld })

  useCanvasKeyboardShortcuts({
    applyPanScroll,
    bleedHeight,
    bleedWidth,
    isInShapeCreationMode,
    isInPenToolMode,
    isNodeEditMode,
    isInTextCreationMode,
    activeItem,
    isPanInteractionActive,
    isPanModeActive,
    isTextToolActive,
    isEditableTarget,
    cancelTextLinking,
    isTextLinkingActive,
    canSelectShapes,
    onDeactivatePanMode,
    onSelectAllApplied,
    onSelectNoneApplied,
    onBackgroundClick,
    clearShapeSelectionWithHistory,
    clearUnifiedSelectionWithHistory,
    copySelectionToClipboard,
    cutSelectionToClipboard,
    cutSelectionForActiveMode,
    isTextEditSessionActive,
    selectAllForActiveTextEdit,
    handleDeleteKeyWhileTextEditing,
    refocusActiveTextEditor,
    deleteSelectedObjects,
    lockSelectedObjects,
    unlockSelectedObjects,
    toggleFreezeSelectedObjects,
    pasteFromClipboard,
    onDeleteShapesHandled,
    updateGuides,
    nudgeDistancePt,
    onShapesNudge,
    onLayerReorder,
    onUnifiedLayerArrange,
    onActivateTextTool,
    onActivateSelectTool,
    activeIsolationGroupId,
    exitGroupIsolationMode,
    onFreezeSelected,
    nodeEditShapeId,
    onNodeEditModeChange,
    canUnifiedBringForward,
    canUnifiedSendBackward,
    setGroupIsolationPath,
    setNodeEditShapeData,
    setNodeEditShapeId,
    exitNodeEditMode,
    internalShapeManagerRef,
    scrollRef,
    selectedGuideIdsRef,
    setSelectedGuideIds,
    zMarqueeKeyHeldRef,
    setZMarqueeKeyHeld,
    setIsMarqueeZoomMode,
    setMarqueeZoom,
  })
}
